<template>
  <div>
    <FilterDrawer :show="showFilter" @update:show="$emit('update:showFilter', $event)">
      <AgenturDublettenFilter
        @onClickSaveFilter="$emit('save-filter')"
        @onClickResetFilter="$emit('reset-filter')"
      ></AgenturDublettenFilter>
    </FilterDrawer>
    <div class="d-flex flex-column flex-lg-row">
      <div v-if="agenturDuplicates" class="flex-lg-row-fluid">
        <div
          v-for="agenturDuplicate in agenturDuplicates"
          :key="agenturDuplicate.id"
          class="card card-custom w-100 gutter-b confirmation-card"
          :class="{
            'overlay overlay-block overlay-layer bg-dark-o-10': isBusy,
            hidden: agenturDuplicate.hidden,
          }"
        >
          <div class="card-header d-flex align-items-center justify-content-between">
            <span class="state-badge" :class="agenturDuplicate.state.toLowerCase()">
              {{ $t('DUBLETTEN.CONFIRMATION.' + agenturDuplicate.state.toUpperCase()) }}
            </span>
            <div class="d-flex align-items-center text-muted">
              <CalendarSync class="mr-2" :size="14" />
              <span class="mr-6">{{ agenturDuplicate.updatedDateUtc | dateTime }}</span>
              <FileCheck2 class="mr-2" :size="14" />
              <span class="mr-6">{{ agenturDuplicate.createdDateUtc | dateTime }}</span>
              <UserRoundPen class="mr-2" :size="14" />
              <span>{{ $store.getters.getUsernameById(agenturDuplicate.bearbeiter) || '-' }}</span>
            </div>
          </div>

          <div class="card-body">
            <AgenturDuplicatesCompareTable
              :agentur1="agenturDuplicate.agentur1"
              :agentur2="agenturDuplicate.agentur2"
            />
            <div v-if="mergeErrors[duplicateKey(agenturDuplicate)]" class="agentur-duplicates__fehler mt-4">
              <span class="agentur-duplicates__fehler-icon"><TriangleAlert :size="16" /></span>
              <span>{{ mergeErrors[duplicateKey(agenturDuplicate)] }}</span>
            </div>
          </div>

          <div v-if="hasActions(agenturDuplicate.state)" class="card-footer d-flex justify-content-center">
            <b-button
              class="footer-button dialog-btn-secondary"
              v-if="agenturDuplicate.state === 'New'"
              variant="secondary"
              @click="onSendValidationClick(agenturDuplicate, 1)"
            >
              Ablehnen
            </b-button>
            <b-button
              class="footer-button dialog-btn"
              v-if="agenturDuplicate.state === 'New' || agenturDuplicate.state === 'Declined'"
              variant="primary"
              @click="onSendValidationClick(agenturDuplicate, 0)"
            >
              {{ agenturDuplicate.state === 'New' ? 'Annehmen' : 'Korrektur: Annehmen' }}
            </b-button>
            <b-button
              class="footer-button dialog-btn"
              v-if="agenturDuplicate.state === 'Merged'"
              variant="warning"
              @click="onSendValidationClick(agenturDuplicate, 2)"
            >
              Merge rückgängig machen
            </b-button>
          </div>
        </div>

        <div v-if="count > top || count === 0" class="card card-custom">
          <div class="card-body">
            <LoadSpinnerWidget
              v-if="count === 0"
              :dataAvailable="count !== 0"
              :isLoading="false"
              :showText="true"
            ></LoadSpinnerWidget>
            <SKRPagination
              v-if="count > top"
              :overwriteCurrentPage="currentPage"
              :totalRows="count"
              :isLoading="isBusy"
              :perPageOverwrite="top"
              :currentRows="agenturDuplicates.length"
              @pageChange="onPageChange"
            ></SKRPagination>
          </div>
        </div>
      </div>
      <div v-else class="w-100">
        <b-skeleton v-for="n in top" :key="n" class="gutter-b" width="100%" height="377px"></b-skeleton>
      </div>
    </div>
  </div>
</template>

<script>
import {
  GET_AGENTUR_DUPLICATES,
  AGENTUR_DUPLICATES_PAGE_CHANGED,
  SAVE_AGENTUR_DUPLICATES_MERGE_STATE,
} from '@/core/agenturen/stores/agenturDuplicates.module';

import { mapState } from 'vuex';

import LoadSpinnerWidget from '@/components/common/load-spinner-widget.vue';
import SKRPagination from '@/components/common/skr-pagination.vue';

import AgenturDuplicatesCompareTable from '@/components/agenturen/agentur-duplicates-compare-table.vue';
import AgenturDublettenFilter from '@/components/agenturen/agentur-dubletten-filter.vue';
import FilterDrawer from '@/components/common/filter-drawer.vue';

import { extractResponseExceptionMessage } from '@/core/common/services/error-handler.service';
import { CalendarSync, FileCheck2, TriangleAlert, UserRoundPen } from 'lucide-vue';

export default {
  components: {
    LoadSpinnerWidget,
    SKRPagination,
    AgenturDuplicatesCompareTable,
    AgenturDublettenFilter,
    FilterDrawer,
    CalendarSync,
    FileCheck2,
    TriangleAlert,
    UserRoundPen,
  },
  props: {
    showFilter: { type: Boolean, default: false },
  },
  data() {
    return {
      currentPage: 1,
      mergeErrors: {},
    };
  },
  computed: {
    ...mapState({
      isBusy: state => state.agenturDuplicates.isBusy,
      agenturDuplicates: state => state.agenturDuplicates.agenturDuplicates,
      orderBy: state => state.agenturDuplicates.orderBy,
      count: state => state.agenturDuplicates.count,
      top: state => state.agenturDuplicates.top,
    }),
  },
  watch: {
    agenturDuplicates() {
      this.mergeErrors = {};
    },
  },
  mounted() {
    this.$store.dispatch(GET_AGENTUR_DUPLICATES);
  },
  methods: {
    hasActions(state) {
      return state === 'New' || state === 'Declined' || state === 'Merged';
    },
    duplicateKey(agenturDuplicate) {
      return `${agenturDuplicate.agentur1.id}-${agenturDuplicate.agentur2.id}`;
    },
    onSendValidationClick(agenturDuplicate, trigger) {
      const key = this.duplicateKey(agenturDuplicate);
      this.$delete(this.mergeErrors, key);
      this.$store
        .dispatch(SAVE_AGENTUR_DUPLICATES_MERGE_STATE, {
          agentur1Id: agenturDuplicate.agentur1.id,
          agentur2Id: agenturDuplicate.agentur2.id,
          trigger: trigger,
        })
        .then(() => {
          if (trigger === 0) {
            this.toast('Agenturen wurden zusammengeführt.', { variant: 'success' });
          }
        })
        .catch(error => {
          this.$set(
            this.mergeErrors,
            key,
            extractResponseExceptionMessage(error.response?.data) ||
              'Die Aktion konnte nicht ausgeführt werden.'
          );
        });
    },
    onPageChange(ctx) {
      this.$store.dispatch(AGENTUR_DUPLICATES_PAGE_CHANGED, { pageContext: ctx });
      this.currentPage = ctx.page;
      window.scrollTo(0, 0);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/sass/components/_variables.bootstrap.scss';

.agentur-duplicates__fehler {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: var(--radius-sm);
  background-color: #f64e6026;
  border: 1px solid var(--danger);
  font-size: 14px;
  line-height: 20px;
  color: #c10007;
}

.agentur-duplicates__fehler-icon {
  display: inline-flex;
  align-items: center;
  color: #c10007;
}

.confirmation-card > .card-header {
  min-height: 50px;
  padding: 0 24px;
}

.state-badge {
  padding: 10px 16px;
  border-radius: 5px;
  border-width: 1.5px;
  border-style: solid;
  font-weight: 700;
  font-size: 14px;
  line-height: 14px;
  color: #4b4b4b;

  &.new {
    border-color: var(--primary);
  }
  &.merged {
    border-color: #20c997;
  }
  &.declined {
    border-color: var(--danger);
  }
}

.card-body {
  background-color: var(--surface-hover);
}

.card-footer {
  height: 86px;
  gap: 8px;
  padding: 24px;
}

.footer-button {
  min-width: 116px;
  border-radius: 8px;
  align-self: center;
}

.confirmation-card {
  transition: 0.3s ease-out;
  opacity: 1;
  &.hidden {
    opacity: 0;
    left: 10px;
  }
}
</style>
